import { useStore } from '@tanstack/react-form'
import { formatDistanceToNow } from 'date-fns'
import { Check, Copy, TriangleAlert } from 'lucide-react'
import { generatePath } from 'react-router-dom'
import { z } from 'zod'

import {
  formatCurrency,
  formatInstantDate,
  type GetPatientSubscriptionCancelOptionsResponse,
  type GetPatientSubscriptionCancelQuery,
  type GetPatientSubscriptionCancelResponse,
  type PostPatientSubscriptionCancelBody,
  type RegionId,
  subscriptionCancellationPolicySchema,
} from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FormattedCalendarDate, useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { DialogFooter } from '@/frontend/components/ui/dialog'
import { Spinner } from '@/frontend/components/ui/spinner'
import { InvoiceList } from '@/frontend/features/patient/invoice-list'
import { useGetPatientSubscriptionCancel } from '@/frontend/hooks/api/patients/use-get-patient-subscription-cancel'
import { useGetPatientSubscriptionCancelOptions } from '@/frontend/hooks/api/patients/use-get-patient-subscription-cancel-options'
import { usePostPatientSubscriptionCancel } from '@/frontend/hooks/api/patients/use-post-patient-subscription-cancel'
import { useCopyToClipboard } from '@/frontend/hooks/use-copy-to-clipboard'
import { wikiPages } from '@/frontend/lib/wiki'
import { routes } from '@/frontend/routes'

interface MembershipCancellationFormProps {
  patientId: string
  memberEmail: string
  memberRegion: RegionId
  onPendingChange: (pending: boolean) => void
}

const formSchema = z.object({
  cancellationPolicy: subscriptionCancellationPolicySchema,
  waiveFee: z.boolean(),
  refundUnusedCoverage: z.boolean(),
})
type FormValues = z.output<typeof formSchema>

const cancellationPolicyLabels = {
  standard: 'Standard cancellation',
  no_usage: 'No usage',
  one_appointment_only: 'First visit only',
}

export function MembershipCancellationForm(props: MembershipCancellationFormProps) {
  const context = useGetPatientSubscriptionCancelOptions(props.patientId)
  if (context.isPending || context.isError)
    return (
      <div className="min-h-0 space-y-4 overflow-y-auto">
        {context.isPending ? (
          <div role="status" aria-label="Loading cancellation details">
            <Spinner />
          </div>
        ) : (
          <Alert variant="destructive">
            <AlertDescription>
              {context.error.name === 'ConflictError' ? (
                <>
                  Couldn't load cancellation form for this subscription: {context.error.message}{' '}
                  Please contact the engineering team.
                </>
              ) : (
                context.error.message
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>
    )
  return <MembershipCancellationFormInner {...props} context={context.data} />
}

function MembershipCancellationFormInner({
  patientId,
  memberEmail,
  memberRegion,
  onPendingChange,
  context,
}: MembershipCancellationFormProps & { context: GetPatientSubscriptionCancelOptionsResponse }) {
  const cancellation = usePostPatientSubscriptionCancel(patientId)
  const { copied, copy } = useCopyToClipboard()
  const viewerFormat = useViewerFormat()
  const form = useAppForm({
    defaultValues: {
      cancellationPolicy: context.suggestedPolicy,
      waiveFee: false,
      refundUnusedCoverage: false,
    } as FormValues,
    validators: { onChange: formSchema, onSubmit: formSchema },
    listeners: {
      onChange: () => {
        if (cancellation.error?.name === 'AutomaticCancellationUnavailableError')
          cancellation.reset()
      },
    },
    onSubmit: ({ value }) => {
      if (value.waiveFee || value.refundUnusedCoverage) return
      const body: PostPatientSubscriptionCancelBody = {
        cancellationPolicy: value.cancellationPolicy,
        waiveFee: value.waiveFee,
        refundUnusedCoverage: value.refundUnusedCoverage,
      }
      onPendingChange(true)
      cancellation.mutate(body, { onSettled: () => onPendingChange(false) })
    },
  })
  const cancellationPolicy = useStore(form.store, (state) => state.values.cancellationPolicy)
  const waiveFee = useStore(form.store, (state) => state.values.waiveFee)
  const refundUnusedCoverage = useStore(form.store, (state) => state.values.refundUnusedCoverage)
  const configuration: GetPatientSubscriptionCancelQuery | null =
    waiveFee || refundUnusedCoverage
      ? null
      : {
          cancellationPolicy,
          waiveFee: 'false',
          refundUnusedCoverage: 'false',
        }
  const quote = useGetPatientSubscriptionCancel(patientId, configuration)
  const automaticCancellationError =
    cancellation.error?.name === 'AutomaticCancellationUnavailableError'
      ? cancellation.error
      : !quote.isFetching && quote.error?.name === 'AutomaticCancellationUnavailableError'
        ? quote.error
        : null
  const canCancel =
    !waiveFee &&
    !refundUnusedCoverage &&
    !automaticCancellationError &&
    quote.isSuccess &&
    !quote.isFetching
  const request = formatManualCancellationRequest(
    patientId,
    { cancellationPolicy, waiveFee, refundUnusedCoverage },
    automaticCancellationError?.message
  )
  const result = cancellation.data?.cancellation
  const refundAmount = result?.refund
    ? formatCurrency(result.refund.amount, result.refund.currency)
    : null
  if (result)
    return (
      <div className="min-h-0 space-y-4 overflow-y-auto">
        <Alert
          variant={
            result.refund?.status === 'failed' || result.invoiceFailed ? 'destructive' : 'success'
          }
        >
          <AlertTitle>
            {result.refund?.status === 'failed'
              ? 'Refund needs attention'
              : result.invoiceFailed
                ? 'Cancellation fee needs attention'
                : cancellation.data?.subscription.status === 'canceled'
                  ? 'Membership cancelled'
                  : 'Cancellation scheduled'}
          </AlertTitle>
          <AlertDescription>
            {result.refund?.status === 'failed'
              ? `Cancellation completed, but the ${refundAmount} refund needs engineering attention. Check Stripe.`
              : result.invoiceFailed
                ? 'Cancellation completed but the fee invoice could not be created. Speak to engineering.'
                : result.refund
                  ? result.refund.status === 'pending'
                    ? `The ${refundAmount} refund is pending.`
                    : `The ${refundAmount} refund has been issued.`
                  : result.invoice
                    ? 'Check the cancellation invoice below for its payment status.'
                    : 'No charge or refund due.'}
            {cancellation.data?.subscription.cancelAt && (
              <p>
                Membership ends on{' '}
                {formatInstantDate(cancellation.data.subscription.cancelAt, viewerFormat)}.
              </p>
            )}
          </AlertDescription>
        </Alert>
        {result.invoice && (
          <InvoiceList
            invoices={[result.invoice]}
            patientId={patientId}
            memberEmail={memberEmail}
            memberRegion={memberRegion}
          />
        )}
      </div>
    )
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        form.handleSubmit()
      }}
      className="flex min-h-0 flex-col"
    >
      <form.AppForm>
        <div className="min-h-0 space-y-4 overflow-y-auto pr-3 pb-4 [scrollbar-gutter:stable]">
          <p className="text-sm text-muted-foreground">
            Choose a cancellation policy to see when the membership will end and any charge or
            refund due. Review the details before confirming the cancellation.
          </p>
          <Alert className="bg-muted" role="region" aria-label="Membership details">
            <AlertTitle className="mb-1.5">Membership details</AlertTitle>
            <AlertDescription>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
                <div>
                  <dt className="font-semibold text-foreground">Country</dt>
                  <dd>{context.region === 'uk' ? 'UK' : 'US'}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">Plan</dt>
                  <dd>
                    {context.plan === 'monthly'
                      ? 'Monthly'
                      : context.plan === 'annual'
                        ? 'Annual'
                        : 'Two-yearly'}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">Membership start date</dt>
                  <dd className="flex flex-wrap items-center gap-2">
                    <FormattedCalendarDate value={context.membershipStartDate} />
                    {context.subscription.effectiveStartDate && (
                      <Badge variant="info" size="sm">
                        Manually set
                      </Badge>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">
                    {new Date(context.trialEndsAt).getTime() <= Date.now()
                      ? '28-day trial ended'
                      : '28-day trial ends'}
                  </dt>
                  <dd>{formatInstantDate(context.trialEndsAt, viewerFormat)}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">
                    {new Date(context.initialCommitmentEndsAt).getTime() <= Date.now()
                      ? 'Initial commitment ended'
                      : 'Initial commitment ends'}
                  </dt>
                  <dd>{formatInstantDate(context.initialCommitmentEndsAt, viewerFormat)}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">Paid through</dt>
                  <dd>{formatInstantDate(context.currentPeriodEndsAt, viewerFormat)}</dd>
                </div>
                <div className="sm:col-span-3">
                  <dt className="font-semibold text-foreground">Appointments</dt>
                  <dd>
                    {context.appointmentCount === 0
                      ? 'No appointments'
                      : context.appointmentCount === 1
                        ? context.firstAppointment
                          ? `One appointment, ${formatDistanceToNow(new Date(context.firstAppointment.startTime), { addSuffix: true })}`
                          : 'One appointment'
                        : `${context.appointmentCount} appointments`}
                  </dd>
                </div>
              </dl>
            </AlertDescription>
          </Alert>
          <fieldset disabled={cancellation.isPending} className="space-y-4">
            <form.AppField name="cancellationPolicy">
              {(field) => (
                <field.RadioGroupField
                  variant="radio"
                  label="Cancellation policy"
                  description={
                    <>
                      Choose a policy based on when the member requested cancellation, not when you
                      process it. Check the{' '}
                      <a
                        href={wikiPages.membershipCancellation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-4"
                      >
                        cancellation policy
                      </a>{' '}
                      for full eligibility rules.
                    </>
                  }
                  items={context.availablePolicies.map((policy) => ({
                    value: policy,
                    label: (
                      <span className="flex flex-col ml-2.5">
                        <span className="text-sm font-semibold">
                          {cancellationPolicyLabels[policy]}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {
                            {
                              standard: 'Standard cancellation terms apply.',
                              no_usage:
                                'No attended appointments; cancellation requested within the 28-day trial.',
                              one_appointment_only:
                                'The member attended one appointment within 28 days of joining and asked to cancel within 48 hours of that visit.',
                            }[policy]
                          }
                        </span>
                        {field.state.value === policy &&
                          context.policyWarnings[policy].map((warning) => (
                            <span
                              key={warning}
                              className="flex items-center gap-1 text-xs font-medium text-warning-accent"
                            >
                              <TriangleAlert className="size-3 shrink-0" aria-hidden />
                              <span>{warning}</span>
                            </span>
                          ))}
                      </span>
                    ),
                  }))}
                />
              )}
            </form.AppField>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Additional adjustments</h2>
              <form.AppField name="refundUnusedCoverage">
                {(field) => (
                  <field.CheckboxField
                    label="Refund member for unused coverage?"
                    description="E.g. in the case of a PTS, or an error on Snoots' part."
                  />
                )}
              </form.AppField>
              <form.AppField name="waiveFee">
                {(field) => (
                  <field.CheckboxField
                    label="Waive charge or consultation fee"
                    description="Waivers and approval-dependent refunds require engineering assistance."
                  />
                )}
              </form.AppField>
            </section>

            <form.GlobalError />
            <div className="space-y-4">
              {waiveFee || refundUnusedCoverage || automaticCancellationError ? (
                <Alert variant="warning">
                  <AlertTitle className="mb-1.5">Manual cancellation required</AlertTitle>
                  <AlertDescription className="space-y-1">
                    <div>The requested cancellation cannot be processed automatically:</div>
                    <ul className="list-disc space-y-1 pl-5 font-semibold">
                      {describeAutomaticCancellationBlockers(
                        { waiveFee, refundUnusedCoverage },
                        automaticCancellationError?.message
                      ).map((blocker) => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </ul>
                    <div>Please request manual cancellation on Slack.</div>
                  </AlertDescription>
                </Alert>
              ) : quote.isPending || quote.isFetching ? (
                <div role="status" aria-label="Calculating cancellation">
                  <Spinner />
                </div>
              ) : quote.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>{quote.error.message}</AlertDescription>
                </Alert>
              ) : (
                <CancellationSummary preview={quote.data} />
              )}
              {cancellation.error &&
                cancellation.error.name !== 'AutomaticCancellationUnavailableError' && (
                  <Alert variant="destructive">
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>{cancellation.error.message}</AlertDescription>
                  </Alert>
                )}
            </div>
          </fieldset>
        </div>
        <DialogFooter className="shrink-0 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={cancellation.isPending}
            onClick={() =>
              copy(
                request,
                'Manual cancellation request',
                'Copied manual cancellation request to clipboard'
              )
            }
          >
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            Copy manual cancellation request
          </Button>
          <form.ConfirmSubmitButton
            type="button"
            variant="destructive"
            dialogColor="destructive"
            heading="Confirm membership cancellation?"
            confirmLabel={
              quote.data?.endsImmediately ? 'Cancel membership now' : 'Schedule cancellation'
            }
            disabled={cancellation.isPending || !canCancel}
            confirmPending={cancellation.isPending}
            description={quote.data && <CancellationSummary preview={quote.data} />}
          >
            Cancel membership
          </form.ConfirmSubmitButton>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}

function describeAutomaticCancellationBlockers(
  { waiveFee, refundUnusedCoverage }: Pick<FormValues, 'waiveFee' | 'refundUnusedCoverage'>,
  errorMessage?: string
) {
  return [
    ...(waiveFee ? ['Waiving the charge or consultation fee requires manual cancellation.'] : []),
    ...(refundUnusedCoverage ? ['Refunding unused coverage requires manual cancellation.'] : []),
    ...(errorMessage ? [errorMessage] : []),
  ]
}

function formatManualCancellationRequest(
  patientId: string,
  {
    cancellationPolicy,
    waiveFee,
    refundUnusedCoverage,
  }: Pick<FormValues, 'cancellationPolicy' | 'waiveFee' | 'refundUnusedCoverage'>,
  errorMessage?: string
) {
  const automaticCancellationBlockers = describeAutomaticCancellationBlockers(
    { waiveFee, refundUnusedCoverage },
    errorMessage
  )
  const patientUrl = new URL(generatePath(routes.patient, { patientId }), window.location.origin)
    .href
  return [
    '**Manual membership cancellation request**',
    '',
    `Patient: ${patientUrl}`,
    `Cancellation policy: ${cancellationPolicyLabels[cancellationPolicy]}`,
    `Refund unused coverage: ${refundUnusedCoverage ? 'Yes' : 'No'}`,
    `Waive charge or consultation fee: ${waiveFee ? 'Yes' : 'No'}`,
    ...(automaticCancellationBlockers.length > 0
      ? ['', 'Blockers:', ...automaticCancellationBlockers.map((blocker) => `- ${blocker}`)]
      : []),
  ].join('\n')
}

function CancellationSummary({ preview }: { preview: GetPatientSubscriptionCancelResponse }) {
  const viewerFormat = useViewerFormat()
  const { breakdown, settlement, currency } = preview
  return (
    <Alert variant="info">
      <AlertTitle className="mb-1.5 flex items-center gap-1.5">Cancellation outcome</AlertTitle>
      <AlertDescription className="space-y-3">
        <dl className="space-y-1">
          <div className="flex justify-between gap-6">
            <dt className="font-semibold">Membership ends</dt>
            <dd className="text-right">
              {preview.endsImmediately
                ? 'Immediately'
                : formatInstantDate(preview.effectiveAt, viewerFormat)}
            </dd>
          </div>
          {breakdown.map((line) => (
            <div key={line.description} className="flex justify-between gap-6">
              <dt>{line.description}</dt>
              <dd className="text-right tabular-nums">{formatCurrency(line.amount, currency)}</dd>
            </div>
          ))}
          {settlement.refundAmount > 0 ? (
            <div className="flex justify-between gap-6 border-t pt-3 font-semibold">
              <dt>Total refund</dt>
              <dd className="text-right tabular-nums">
                {formatCurrency(settlement.refundAmount, currency)}
              </dd>
            </div>
          ) : settlement.cancellationCharge > 0 ? (
            <div className="flex justify-between gap-6 border-t pt-3 font-semibold">
              <dt>Total charge</dt>
              <dd className="text-right tabular-nums">
                {formatCurrency(-settlement.cancellationCharge, currency)}
              </dd>
            </div>
          ) : null}
        </dl>
        {settlement.refundAmount === 0 && settlement.cancellationCharge === 0 && (
          <p className="border-t pt-3 text-right font-semibold">No charge or refund required</p>
        )}
      </AlertDescription>
    </Alert>
  )
}
